import { motion } from 'motion/react';
import { Brain, Zap, Sparkles, Mail, Package, Languages, Smile, MousePointerClick } from 'lucide-react';

const features = [
  {
    icon: Brain,
    title: 'Smart Context Understanding',
    description: 'Analyzes email threads and conversation history to craft contextually perfect replies that continue the conversation naturally.'
  },
  {
    icon: Zap,
    title: 'Multiple Specialized Variants',
    description: 'Get 5 tailored draft options instantly — from formal to friendly, concise to detailed. Choose what fits your style and situation.'
  },
  {
    icon: Sparkles,
    title: 'Powered by Grok (Pro) or GPT-4o-mini (Free)',
    description: 'Pro users unlock Grok-4 for superior reasoning and nuanced responses. Free tier powered by GPT-4o-mini for everyday needs.'
  },
  {
    icon: Mail,
    title: 'Works in Gmail & LinkedIn',
    description: 'Seamlessly integrated into your workflow. Draft responses directly in Gmail compose and LinkedIn messaging without switching tabs.'
  },
  {
    icon: Package,
    title: 'Specialized Professional Packages',
    description: 'Tailored prompt sets for your role — Sales, Recruitment, Job Search, and more. Unlock expertly designed variants and tones optimized for your specific workflow, delivering higher response rates and professional impact.'
  },
  {
    icon: Languages,
    title: 'Multilingual Support',
    description: 'Draft replies in the language of the original email — English, Russian, Spanish, French, German, and 30+ more. xRepl.ai detects the language automatically and generates natural, idiomatic responses that respect cultural nuance.'
  },
  {
    icon: Smile,
    title: 'Smart Tone Adaptation',
    description: 'Automatically matches the perfect professional tone — formal, confident, empathetic, enthusiastic, or grateful — based on context, relationship, and email type. Always sounds human and appropriate, never generic or off-key.'
  },
  {
    icon: MousePointerClick,
    title: 'One-Click Insert & Edit',
    description: 'Insert any draft into your compose window with a single click. Full formatting preserved, cursor placed at the end for instant editing or sending — seamless and lightning fast.'
  }
];

export function Features() {
  return (
    <section className="py-20 bg-white/80 dark:bg-[#0A1628]/60 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Powerful Features
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Everything you need to craft perfect responses in seconds
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              className="group p-6 bg-gray-50 dark:bg-[#1E3A8A]/10 rounded-2xl border border-gray-200 dark:border-[#1E3A8A]/30 hover:border-[#60A5FA] dark:hover:border-[#60A5FA] transition-all hover:shadow-xl hover:shadow-[#60A5FA]/10 cursor-pointer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -5 }}
            >
              <div className="w-12 h-12 bg-[#1E3A8A] dark:bg-[#60A5FA] rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <feature.icon className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                {feature.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}